import { Link } from "@tanstack/react-router";
import { ArrowLeft, LayoutGrid, MapPin, Plus, Rows3 } from "lucide-react";
import { useLingui } from "@lingui/react/macro";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import type { ViewCounts } from "@/lib/multi-view";
import { SegmentedControl } from "./SegmentedControl";

export type MultiViewMode = "tabs" | "multi";
export type MultiViewLayout = "grid" | "spotlight";

interface MultiViewHeaderProps {
  locationName?: string;
  counts: ViewCounts;
  view: MultiViewMode;
  onViewChange: (view: MultiViewMode) => void;
  layout: MultiViewLayout;
  onLayoutChange: (layout: MultiViewLayout) => void;
  autoFocusLate: boolean;
  onAutoFocusLateChange: (value: boolean) => void;
  onAddRecipes: () => void;
}

/**
 * Back to Prep, the screen's name with where and how many, and the controls:
 * Tabs or Multi view, Grid or Spotlight, auto-focus on late, and Add recipes.
 */
export function MultiViewHeader({
  locationName,
  counts,
  view,
  onViewChange,
  layout,
  onLayoutChange,
  autoFocusLate,
  onAutoFocusLateChange,
  onAddRecipes,
}: MultiViewHeaderProps) {
  const { t } = useLingui();
  return (
    <header className="flex flex-wrap items-center justify-between gap-4">
      <div className="flex items-center gap-3">
        <Link
          to="/prep"
          title={t`Back to Prep`}
          aria-label={t`Back to Prep`}
          className="flex size-10 shrink-0 items-center justify-center rounded-lg border border-(--color-border) bg-(--color-surface-content) text-(--color-text-primary) hover:bg-(--color-surface-muted)"
        >
          <ArrowLeft className="size-4.5" />
        </Link>
        <div className="flex flex-col">
          <h1 className="text-sm font-bold tracking-widest text-(--color-text-primary) uppercase">{t`Multi View`}</h1>
          <p className="flex items-center gap-1.5 text-sm text-(--color-text-secondary)" data-testid="multi-view-counts">
            {locationName && (
              <>
                <MapPin className="size-3.5 shrink-0" />
                <span>{locationName}</span>
                <span aria-hidden>·</span>
              </>
            )}
            <span>{t`${counts.open} open`}</span>
            <span aria-hidden>·</span>
            <span className={counts.late > 0 ? "font-semibold text-(--color-danger-text)" : undefined}>{t`${counts.late} late`}</span>
          </p>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <SegmentedControl<MultiViewMode>
          aria-label={t`View`}
          value={view}
          onChange={onViewChange}
          options={[
            { value: "tabs", label: t`Tabs`, icon: <Rows3 className="size-4" /> },
            { value: "multi", label: t`Multi view`, icon: <LayoutGrid className="size-4" /> },
          ]}
        />
        {view === "multi" && (
          <>
            <SegmentedControl<MultiViewLayout>
              aria-label={t`Layout`}
              value={layout}
              onChange={onLayoutChange}
              options={[
                { value: "grid", label: t`Grid` },
                { value: "spotlight", label: t`Spotlight` },
              ]}
            />
            <label className="flex cursor-pointer items-center gap-2 text-sm font-medium text-(--color-text-primary)">
              <Switch checked={autoFocusLate} onCheckedChange={onAutoFocusLateChange} aria-label={t`Auto-focus late`} />
              {t`Auto-focus late`}
            </label>
            <Button type="button" variant="brand-outline" onClick={onAddRecipes} className="gap-1.5">
              <Plus />
              {t`Add recipes`}
            </Button>
          </>
        )}
      </div>
    </header>
  );
}
